import {
  Component,
  inject,
  input,
  output,
  signal,
  OnChanges,
  SimpleChanges,
  computed,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { ButtonDirective } from '../../../../components/button/button.directive';
import { InputComponent } from '../../../../components/input/input.component';
import { SelectComponent } from '../../../../components/select/select.component';
import { ModalComponent } from '../../../../components/modal/modal.component';
import { DictionaryService } from '../../../../services/dictionary.service';
import { environment } from '../../../../../environments/environment';
import { SnackbarService } from '../../../../services/snackbar.service';
import { CustomerDropdownCacheService } from '../../../../services/customer-dropdown-cache.service';
import type {
  CustomerType,
  CustomerApiPayload,
  CustomerCreationDraftRequest,
  ApiCustomerType,
  HospitalDoctor,
  FilterOption,
} from './customer-create-modal.interface';

@Component({
  selector: 'app-customer-create-modal',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonDirective,
    InputComponent,
    SelectComponent,
    ModalComponent,
  ],
  templateUrl: './customer-create-modal.component.html',
  styleUrls: ['./customer-create-modal.component.scss'],
})
export class CustomerCreateModalComponent implements OnChanges {
  protected dictionaryService = inject(DictionaryService);
  protected dropdownCache = inject(CustomerDropdownCacheService);
  private readonly emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  @ViewChildren(SelectComponent) private selectComponents?: QueryList<SelectComponent>;

  // Inputs
  isOpen = input<boolean>(false);
  customerType = input<CustomerType>('doctor');

  // Outputs
  onClose = output<void>();
  onSubmitApi = output<CustomerCreationDraftRequest>(); // API-formatted payload

  // Validation display state
  hasSubmitted = signal<boolean>(false);
  touchedSelects = signal<Record<string, boolean>>({});

  // Local dropdown options
  cityOptions = signal<string[]>([]);

  // Selected values for selects
  selectedCountry = signal<string>('');
  selectedProvince = signal<string>('');
  selectedCity = signal<string>('');
  selectedClassType = signal<string>('');
  selectedMktClass = signal<string>('');
  selectedSpecialClass = signal<string>('');
  selectedSpeciality = signal<string>('');

  // Hospital doctors list (for hospital type only)
  hospitalDoctors = signal<HospitalDoctor[]>([
    { name: '', speciality: '' },
  ]);

  isDropdownDataReady = this.dropdownCache.isReady;

  // Computed: Province select is disabled until country is selected
  isProvinceDisabled = computed(() => !this.selectedCountry());
  // Computed: City select is disabled until province is selected
  isCityDisabled = computed(() => !this.selectedProvince());

  // Computed modal title based on customer type
  modalTitle = computed(() => {
    const type = this.customerType();
    switch (type) {
      case 'doctor':
        return this.dictionaryService.get('addDoctor') || 'Add Doctor';
      case 'provider':
        return this.dictionaryService.get('addProvider') || 'Add Provider';
      case 'pharmacy':
        return this.dictionaryService.get('addPharmacy') || 'Add Pharmacy';
      case 'hospital':
        return this.dictionaryService.get('addHospital') || 'Add Hospital';
      default:
        return 'Add Customer';
    }
  });

  // Form
  protected createForm = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>(''),
    phone: new FormControl<string>(''),
    // Doctor specific
    center: new FormControl<string>(''),
    // Hospital specific
    department: new FormControl<string>(''),
    // Common classification fields
    targetedFrequency: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
    ]),
  });

  ngOnChanges(changes: SimpleChanges): void {
    // Sync modal state with isOpen input
    if (changes['isOpen']) {
      if (this.isOpen()) {
        this.dropdownCache.loadAllOptions();
        this.resetForm();
      }
    }

    // Reset form when customer type changes
    if (changes['customerType'] && !changes['customerType'].firstChange) {
      this.resetForm();
    }
  }

  /**
   * Resets the form to its initial state
   */
  private resetForm(): void {
    this.createForm.reset();
    this.hasSubmitted.set(false);
    this.touchedSelects.set({});
    this.selectedCountry.set('');
    this.selectedProvince.set('');
    this.selectedCity.set('');
    this.selectedClassType.set('');
    this.selectedMktClass.set('');
    this.selectedSpecialClass.set('');
    this.selectedSpeciality.set('');
    this.cityOptions.set([]);
    // Reset hospital doctors to single empty entry
    this.hospitalDoctors.set([{ name: '', speciality: '' }]);
    setTimeout(() => this.resetSelectComponents());
  }

  private resetSelectComponents(): void {
    this.selectComponents?.forEach((select) => {
      select.updateSelectedOption('');
    });
  }

  /**
   * Adds a new doctor entry for hospital form
   */
  addHospitalDoctor(): void {
    this.hospitalDoctors.update((doctors) => [
      ...doctors,
      { name: '', speciality: '' },
    ]);
  }

  /**
   * Removes a doctor entry from hospital form
   */
  removeHospitalDoctor(index: number): void {
    this.hospitalDoctors.update((doctors) => {
      if (doctors.length > 1) {
        return doctors.filter((_, i) => i !== index);
      }
      return doctors; // Keep at least one doctor
    });
  }

  /**
   * Updates a doctor's name in hospital form
   */
  updateHospitalDoctorName(index: number, name: string): void {
    this.hospitalDoctors.update((doctors) => {
      const updated = [...doctors];
      updated[index] = { ...updated[index], name };
      return updated;
    });
  }

  /**
   * Updates a doctor's speciality in hospital form
   */
  updateHospitalDoctorSpeciality(index: number, speciality: string): void {
    this.hospitalDoctors.update((doctors) => {
      const updated = [...doctors];
      updated[index] = { ...updated[index], speciality };
      return updated;
    });
  }

  // Select change handlers
  onCountryChange(option: FilterOption): void {
    this.selectedCountry.set(option.value);
    this.selectedProvince.set('');
    this.selectedCity.set('');
    this.cityOptions.set([]);
  }

  onProvinceChange(option: FilterOption): void {
    this.selectedProvince.set(option.value);
    this.selectedCity.set('');
    this.loadCityOptions(option.value);
  }

  /**
   * Loads city options based on selected province
   */
  private loadCityOptions(provinceName: string): void {
    const cities = this.dropdownCache.getCityOptions(provinceName);
    this.cityOptions.set(cities);
  }

  onCityChange(option: FilterOption): void {
    this.selectedCity.set(option.value);
  }

  onClassTypeChange(option: FilterOption): void {
    this.selectedClassType.set(option.value);
  }

  onMktClassChange(option: FilterOption): void {
    this.selectedMktClass.set(option.value);
  }

  onSpecialClassChange(option: FilterOption): void {
    this.selectedSpecialClass.set(option.value);
  }

  onSpecialityChange(option: FilterOption): void {
    this.selectedSpeciality.set(option.value);
  }

  markSelectTouched(field: string, event: FocusEvent): void {
    const nextFocusedElement = event.relatedTarget as Node | null;
    const selectElement = event.currentTarget as HTMLElement | null;

    if (nextFocusedElement && selectElement?.contains(nextFocusedElement)) {
      return;
    }

    setTimeout(() => {
      this.touchedSelects.update((fields) => ({
        ...fields,
        [field]: true,
      }));
    });
  }

  /**
   * Handles modal close from the generic modal component
   */
  handleModalClose(): void {
    this.onClose.emit();
  }

  /**
   * Submits the form
   */
  submit(): void {
    this.hasSubmitted.set(true);

    if (!this.isFormValid()) {
      this.createForm.markAllAsTouched();
      return;
    }

    // Map internal customer type to API customer type
    const typeMap: Record<CustomerType, ApiCustomerType> = {
      doctor: 'Doctor',
      provider: 'Provider',
      pharmacy: 'Pharmacy',
      hospital: 'Hospital',
    };

    // Build base API payload
    const emailValue = this.createForm.controls.email.value?.trim();
    const phoneValue = this.createForm.controls.phone.value?.trim();
    const mktClassValue = this.selectedMktClass();
    const specialClassValue = this.selectedSpecialClass();

    const customerPayload: CustomerApiPayload = {
      name: this.createForm.controls.name.value || '',
      email: emailValue || undefined,
      phone: phoneValue || undefined,
      location: {
        country: this.selectedCountry(),
        province: this.selectedProvince(),
        city: this.selectedCity(),
      },
      classType: this.selectedClassType(),
      type: typeMap[this.customerType()],
      targetedFrequency: Number(
        this.createForm.controls.targetedFrequency.value,
      ),
    };

    if (mktClassValue) {
      customerPayload.mktClass = mktClassValue;
    }

    if (specialClassValue) {
      customerPayload.specialClass = specialClassValue;
    }

    // Add type-specific fields
    switch (this.customerType()) {
      case 'doctor':
      case 'provider':
        customerPayload.speciality = this.selectedSpeciality();
        this.addCenterIfPresent(customerPayload);
        break;
      case 'pharmacy':
        customerPayload.speciality = this.selectedSpeciality();
        this.addCenterIfPresent(customerPayload);
        customerPayload.departments = ['Pharmacy'];
        break;
      case 'hospital':
        const validDoctors = this.hospitalDoctors().filter(
          (d) => d.name?.trim() && d.speciality?.trim(),
        );

        customerPayload.doctors = validDoctors.map((d) => ({
          name: d.name.trim(),
          speciality: d.speciality.trim(),
        }));

        const departmentValue =
          this.createForm.controls.department.value?.trim();
        if (departmentValue) {
          customerPayload.departments = [departmentValue];
        }
        break;
    }

    // Build the API request body
    const apiRequest: CustomerCreationDraftRequest = {
      customers: [customerPayload],
    };

    // Emit API-formatted payload for direct API calls
    this.onSubmitApi.emit(apiRequest);

    this.handleModalClose();
  }

  private addCenterIfPresent(customerPayload: CustomerApiPayload): void {
    const centerValue = this.createForm.controls.center.value?.trim();

    if (centerValue) {
      customerPayload.center = centerValue;
    }
  }

  /**
   * Checks if form is valid based on customer type
   */
  isFormValid(): boolean {
    const baseValid =
      this.createForm.controls.name.valid &&
      !this.isEmailInvalid() &&
      this.createForm.controls.targetedFrequency.valid &&
      !!this.selectedCountry() &&
      !!this.selectedProvince() &&
      !!this.selectedCity() &&
      !!this.selectedClassType();

    switch (this.customerType()) {
      case 'doctor':
      case 'provider':
      case 'pharmacy':
        // Doctors, providers, and pharmacies require speciality and center
        return (
          baseValid &&
          !!this.selectedSpeciality() &&
          this.hasRequiredCenter()
        );
      case 'hospital':
        const doctors = this.hospitalDoctors();
        const hasValidDoctor = doctors.some(
          (d) => d.name?.trim() && d.speciality?.trim(),
        );
        return baseValid && hasValidDoctor;
      default:
        return baseValid;
    }
  }

  isNameInvalid(): boolean {
    const control = this.createForm.controls.name;
    return control.invalid && (control.dirty || control.touched);
  }

  isCenterInvalid(): boolean {
    const control = this.createForm.controls.center;

    return (
      (this.hasSubmitted() || control.dirty || control.touched) &&
      !this.hasRequiredCenter()
    );
  }

  private hasRequiredCenter(): boolean {
    return !!this.createForm.controls.center.value?.trim();
  }

  isEmailInvalid(): boolean {
    const currentEmail = this.createForm.controls.email.value?.trim() ?? '';
    return !!currentEmail && !this.emailPattern.test(currentEmail);
  }

  isTargetedFrequencyInvalid(): boolean {
    const control = this.createForm.controls.targetedFrequency;
    return control.invalid && (control.dirty || control.touched);
  }

  getTargetedFrequencyErrorMessage(): string {
    const control = this.createForm.controls.targetedFrequency;

    if (control.hasError('required')) {
      return this.dictionaryService.get('Required') || 'This field is required.';
    }

    return (
      this.dictionaryService.get('InvalidTargetedFrequency') ||
      'Targeted frequency must be at least 0.'
    );
  }

  isRequiredSelectInvalid(value: string, field: string): boolean {
    return (this.hasSubmitted() || !!this.touchedSelects()[field]) && !value;
  }

  isHospitalDoctorNameInvalid(index: number): boolean {
    return (
      this.hasSubmitted() && !this.hospitalDoctors()[index]?.name?.trim()
    );
  }

  isHospitalDoctorSpecialityInvalid(index: number): boolean {
    const field = `hospitalDoctorSpeciality-${index}`;

    return (
      (this.hasSubmitted() || !!this.touchedSelects()[field]) &&
      !this.hospitalDoctors()[index]?.speciality?.trim()
    );
  }

  /**
   * Checks if a specific field should be shown based on customer type
   */
  showField(field: string): boolean {
    const type = this.customerType();
    switch (field) {
      case 'speciality':
      case 'center':
        // Show speciality and center for doctors, providers, and pharmacies
        return type === 'doctor' || type === 'provider' || type === 'pharmacy';
      case 'hospitalDoctors':
        // Show doctors section only for hospital
        return type === 'hospital';
      default:
        return true;
    }
  }
}
